import { FinixTokenFormState } from './useFinixTokenForm.js';
import { FinixError, FinixResponse, FinixTokenData } from './types.js';
import { HasSideEffect } from './reducerWithSideEffects';
import { PaymentMethodType } from './PaymentMethodSelector';
import { v4 as uuidv4 } from 'uuid';
import type { SuggestedAmountRule } from '../../types/payments/suggestedAmount.js';

/**
 * Subset of the backend `SuggestedAmount` that the cloud function ships to
 * unauthenticated clients. The backend may attach a `metadata` object for
 * server-side diagnostics; that field is intentionally stripped before this
 * payload reaches the frontend.
 *
 * `rule` is imported from the shared `SuggestedAmountRule` type so adding a
 * new rule on the backend is a compile error here, not a silent drift.
 */
export interface SuggestedAmountFromBackend {
  amountCents: number;
  rule: SuggestedAmountRule;
  label: string;
  description: string;
  invoiceFinalizedTimestamp?: string;
  paymentsReceivedSinceInvoiceCents?: number;
}

export interface OnSubmitPaymentProps {
  environment: 'sandbox' | 'live';
  idempotencyId: string;
  amountCents: number;
  token: string;
  sessionKey: string;
  fingerprint: string;
  sendTransactionCompleteEmails: boolean;
  paymentMethod: PaymentMethodType;
}

export type OnSubmitPaymentResult =
  | {
      status: 'success';
      payload: {
        payFacTransactionId: string;
        paymentInstrument: {
          id: string;
          accountType: string | undefined;
          bankCode: string | undefined;
          lastFour: string | undefined;
          brand: string | undefined;
          cardType: string | undefined;
          expirationMonth: number | undefined;
          expirationYear: number | undefined;
          name: string | undefined;
          maskedAccountNumber: string | undefined;
        };
      };
    }
  | {
      status: 'error';
      userMessage: string;
      shouldResetIdempotencyId: boolean;
    };

type BasePaymentFormState =
  | { status: 'IDLE'; idempotencyId: string }
  | {
      status: 'SUBMITTING';
      formValues: PaymentFormValues;
      idempotencyId: string;
      sideEffect: (dispatch: React.Dispatch<PaymentFormAction>) => void;
    }
  | {
      status: 'SUBMIT_FAILED';
      userMessage: string;
      idempotencyId: string;
    }
  | {
      status: 'SUBMIT_SUCCEEDED';
      amount: number;
      payFacTransactionId: string;
      paymentInstrument: {
        id: string;
        accountType: string | undefined;
        bankCode: string | undefined;
        lastFour: string | undefined;
        brand: string | undefined;
        cardType: string | undefined;
        expirationMonth: number | undefined;
        expirationYear: number | undefined;
        name: string | undefined;
        maskedAccountNumber: string | undefined;
      };
      idempotencyId: string;
    };

export type PaymentFormState = BasePaymentFormState &
  HasSideEffect<PaymentFormAction>;

export interface PaymentFormValues {
  finixEnvironment: 'sandbox' | 'live';
  finixMerchantId: string;
  finixApplicationId: string;
  firmId: string;
  matterId: string;
  amount: number;
  paymentPageId: string;
  paymentMethod: PaymentMethodType;
  savePaymentMethod: boolean;
  sendTransactionCompleteEmails: boolean;
}

export type PaymentFormAction =
  | {
      type: 'SUBMIT_CLICK';
      formValues: PaymentFormValues;
      finixTokenFormState: FinixTokenFormState;
      onSubmitPayment: (
        props: OnSubmitPaymentProps,
      ) => Promise<OnSubmitPaymentResult>;
    }
  | {
      type: 'FINIX_TOKENINZATION_SUCCESS_RECEIVED';
      sessionKey: string;
      token: string;
      tokenData: FinixTokenData;
      formValues: PaymentFormValues;
      onSubmitPayment: (
        props: OnSubmitPaymentProps,
      ) => Promise<OnSubmitPaymentResult>;
    }
  | {
      type: 'FINIX_TOKENINZATION_FAILURE_RECEIVED';
      userMessage: string;
      amount: number;
    }
  | {
      type: 'SUBMIT_SUCCESS_RECEIVED';
      amount: number;
      payFacTransactionId: string;
      paymentInstrument: {
        id: string;
        accountType: string | undefined;
        bankCode: string | undefined;
        lastFour: string | undefined;
        brand: string | undefined;
        cardType: string | undefined;
        expirationMonth: number | undefined;
        expirationYear: number | undefined;
        name: string | undefined;
        maskedAccountNumber: string | undefined;
      };
    }
  | {
      type: 'SUBMIT_FAILURE_RECEIVED';
      userMessage: string;
      amount: number;
      shouldResetIdempotencyId: boolean;
    };

export const initialState: PaymentFormState = {
  status: 'IDLE',
  idempotencyId: uuidv4(),
};

export const paymentFormReducer = (
  state: PaymentFormState,
  action: PaymentFormAction,
): PaymentFormState => {
  switch (action.type) {
    case 'SUBMIT_CLICK':
      return {
        status: 'SUBMITTING',
        formValues: action.formValues,
        idempotencyId: state.idempotencyId,
        sideEffect: async (dispatch) => {
          try {
            submitFinixTokenForm(
              action.formValues,
              action.finixTokenFormState,
              (sessionKey, token, tokenData) => {
                dispatch({
                  type: 'FINIX_TOKENINZATION_SUCCESS_RECEIVED',
                  sessionKey,
                  token,
                  tokenData,
                  formValues: action.formValues,
                  onSubmitPayment: action.onSubmitPayment,
                });
              },
              (error) => {
                dispatch({
                  type: 'FINIX_TOKENINZATION_FAILURE_RECEIVED',
                  userMessage: error.message,
                  amount: action.formValues.amount,
                });
              },
            );
          } catch (error) {
            console.error(`Exception thrown during Finix tokenization:`, error);
            dispatch({
              type: 'FINIX_TOKENINZATION_FAILURE_RECEIVED',
              userMessage: 'Internal error',
              amount: action.formValues.amount,
            });
          }
        },
      };
    case 'FINIX_TOKENINZATION_SUCCESS_RECEIVED':
      return {
        status: 'SUBMITTING',
        formValues: action.formValues,
        idempotencyId: state.idempotencyId,
        sideEffect: async (dispatch) => {
          try {
            const result = await action.onSubmitPayment({
              environment: action.formValues.finixEnvironment,
              idempotencyId: state.idempotencyId,
              token: action.token,
              fingerprint: action.tokenData.fingerprint ?? '',
              sessionKey: action.sessionKey,
              amountCents: Math.round(action.formValues.amount * 100),
              sendTransactionCompleteEmails:
                action.formValues.sendTransactionCompleteEmails,
              paymentMethod: action.formValues.paymentMethod,
            });
            if (result.status === 'success') {
              dispatch({
                type: 'SUBMIT_SUCCESS_RECEIVED',
                amount: action.formValues.amount,
                payFacTransactionId: result.payload.payFacTransactionId,
                paymentInstrument: result.payload.paymentInstrument,
              });
            } else if (result.status === 'error') {
              dispatch({
                type: 'SUBMIT_FAILURE_RECEIVED',
                userMessage: result.userMessage,
                amount: action.formValues.amount,
                shouldResetIdempotencyId: result.shouldResetIdempotencyId,
              });
            } else {
              dispatch({
                type: 'SUBMIT_FAILURE_RECEIVED',
                userMessage: 'Internal error',
                amount: action.formValues.amount,
                shouldResetIdempotencyId: false,
              });
            }
          } catch (error) {
            console.error(`Exception thrown during payment submission:`, error);
            dispatch({
              type: 'SUBMIT_FAILURE_RECEIVED',
              ...buildSubmitFailureFromThrownError(error),
              amount: action.formValues.amount,
            });
          }
        },
      };
    case 'FINIX_TOKENINZATION_FAILURE_RECEIVED':
      return {
        status: 'SUBMIT_FAILED',
        userMessage: action.userMessage,
        // No transaction has been created yet, so it doesn't really matter if we reset the idempotency ID here.
        // Going with a reset just because it feels good.
        idempotencyId: uuidv4(),
      };
    case 'SUBMIT_SUCCESS_RECEIVED':
      return {
        status: 'SUBMIT_SUCCEEDED',
        amount: action.amount,
        payFacTransactionId: action.payFacTransactionId,
        paymentInstrument: action.paymentInstrument,
        idempotencyId: state.idempotencyId,
      };
    case 'SUBMIT_FAILURE_RECEIVED':
      return {
        status: 'SUBMIT_FAILED',
        userMessage: action.userMessage,
        idempotencyId: action.shouldResetIdempotencyId
          ? uuidv4()
          : state.idempotencyId,
      };
  }
};

const buildSubmitFailureFromThrownError = (
  error: unknown,
): {
  userMessage: string;
  shouldResetIdempotencyId: boolean;
} => {
  if (error instanceof Error && error.message.trim().length > 0) {
    return {
      userMessage: error.message,
      // Transport-level failures are ambiguous: the backend may have already
      // attempted the mutation, so keep the current idempotency key.
      shouldResetIdempotencyId: false,
    };
  }

  return {
    userMessage: 'Internal error',
    shouldResetIdempotencyId: false,
  };
};

const submitFinixTokenForm = async (
  formValues: PaymentFormValues,
  finixTokenFormState: FinixTokenFormState,
  onTokenReceived: (
    sessionKey: string,
    token: string,
    tokenData: FinixTokenData,
  ) => void,
  onError: (error: FinixError) => void,
) => {
  if (finixTokenFormState.status !== 'ready') {
    console.error(
      `Finix token form is not ready, status === '${finixTokenFormState.status}'. Bailing`,
    );
    return;
  }
  finixTokenFormState.form.submit(
    formValues.finixEnvironment,
    formValues.finixApplicationId,
    (err: FinixError | null, res?: FinixResponse) => {
      if (err) {
        console.error('Finix tokenization error:', err);
        onError(err);
        return;
      }

      const tokenData = res?.data;
      if (!tokenData) {
        console.error(`No token data received from Finix token form`, res);
        onError({ message: 'No token data received' });
        return;
      }

      const token = tokenData.id;
      console.log(`Token created successfully: ${token}`);
      console.log(`Full response:`, tokenData);

      onTokenReceived(finixTokenFormState.sessionKey, token, tokenData);
    },
  );
};
